import React, { useState, useEffect, useMemo } from 'react';
import { Search, Calendar, Package, RefreshCw, FileText, TrendingUp, Download, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'react-toastify';
import authUtils from '../utils/authUtils';
import * as XLSX from 'xlsx';

const XuatPhuPhamChiTiet = () => {
    // State
    const [dataList, setDataList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [search, setSearch] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [sortConfig, setSortConfig] = useState({ key: 'NGAY_XUAT', direction: 'descending' });

    // Date Range (Safe Initialization)
    const [dateRange, setDateRange] = useState(() => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');

        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const fYear = firstDay.getFullYear();
        const fMonth = String(firstDay.getMonth() + 1).padStart(2, '0');
        const fDay = String(firstDay.getDate()).padStart(2, '0');

        return {
            startDate: `${fYear}-${fMonth}-${fDay}`,
            endDate: `${year}-${month}-${day}`
        };
    });

    // --- Helpers ---
    const formatNumber = (value, digits = 2) => {
        if (!value) return '0';
        return new Intl.NumberFormat('vi-VN', { minimumFractionDigits: 0, maximumFractionDigits: digits }).format(value);
    };

    const formatCurrency = (value) => {
        if (!value) return '0 ₫';
        return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(value);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('vi-VN');
    };

    // --- Fetch Data ---
    const fetchData = async () => {
        setIsLoading(true);
        try {
            // Fetch both Parent and Child tables
            const [parents, children] = await Promise.all([
                authUtils.apiRequest('XUAT_PHUPHAM', 'Find', {}),
                authUtils.apiRequest('XUAT_PHUPHAM_CT', 'Find', {})
            ]);

            const parentList = Array.isArray(parents) ? parents : [];
            const childList = Array.isArray(children) ? children : [];

            // Merge Data
            const merged = childList.map(child => {
                const parent = parentList.find(p => p.SOPHIEU === child.SOPHIEU);
                return {
                    ...child,
                    NGAY_XUAT: parent ? parent.NGAY_XUAT : '',
                    NCC_KHACHHANG: parent ? parent.NCC_KHACHHANG : '',
                    // Ensure numeric values
                    KHOILUONG: parseFloat(child.KHOILUONG) || 0,
                    DONGIA: parseFloat(child.DONGIA) || 0,
                    THANHTIEN: parseFloat(child.THANHTIEN) || 0
                };
            }).filter(item => item.NGAY_XUAT); // Only keep items with valid parent

            setDataList(merged);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Lỗi khi tải dữ liệu chi tiết');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // --- Processing ---
    const filteredItems = useMemo(() => {
        let items = [...dataList];

        // 1. Date Filter
        if (dateRange.startDate && dateRange.endDate) {
            const start = new Date(dateRange.startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(dateRange.endDate);
            end.setHours(23, 59, 59, 999);

            items = items.filter(item => {
                const d = new Date(item.NGAY_XUAT);
                return d >= start && d <= end;
            });
        }

        // 2. Search
        if (search) {
            const lowerSearch = search.toLowerCase();
            items = items.filter(item =>
                (item.SOPHIEU && item.SOPHIEU.toLowerCase().includes(lowerSearch)) ||
                (item.NCC_KHACHHANG && item.NCC_KHACHHANG.toLowerCase().includes(lowerSearch)) ||
                (item.TEN_HANG && item.TEN_HANG.toLowerCase().includes(lowerSearch))
            );
        }

        // 3. Sort
        if (sortConfig.key) {
            items.sort((a, b) => {
                const aVal = a[sortConfig.key];
                const bVal = b[sortConfig.key];

                if (sortConfig.key === 'NGAY_XUAT') {
                    return sortConfig.direction === 'ascending'
                        ? new Date(aVal) - new Date(bVal)
                        : new Date(bVal) - new Date(aVal);
                }

                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return sortConfig.direction === 'ascending' ? aVal - bVal : bVal - aVal;
                }

                if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }

        return items;
    }, [dataList, dateRange, search, sortConfig]);

    // Stats
    const stats = useMemo(() => {
        return {
            totalVolume: filteredItems.reduce((sum, item) => sum + item.KHOILUONG, 0),
            totalMoney: filteredItems.reduce((sum, item) => sum + item.THANHTIEN, 0),
            rowCount: filteredItems.length
        };
    }, [filteredItems]);

    // Pagination
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const paginatedItems = filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    // --- Export Excel ---
    const handleExportExcel = () => {
        const exportData = filteredItems.map((item, index) => ({
            'STT': index + 1,
            'Số phiếu': item.SOPHIEU,
            'Ngày xuất': formatDate(item.NGAY_XUAT),
            'Khách hàng': item.NCC_KHACHHANG,
            'Tên hàng': item.TEN_HANG,
            'Khối lượng (KG)': item.KHOILUONG,
            'Đơn giá': item.DONGIA,
            'Thành tiền': item.THANHTIEN,
            'Ghi chú': item.GHICHU || ''
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "ChiTietXuatPhuPham");
        XLSX.writeFile(wb, `ChiTietXuatPhuPham_${dateRange.startDate}_${dateRange.endDate}.xlsx`);
    };

    const handleRequestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const SortIcon = ({ column }) => {
        if (sortConfig.key !== column) return null;
        return sortConfig.direction === 'ascending' ? <ArrowUp className="w-3 h-3 inline ml-1" /> : <ArrowDown className="w-3 h-3 inline ml-1" />;
    };

    return (
        <div className="p-3 bg-gray-50 min-h-screen font-sans text-gray-800">
            <div className="mx-auto">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-sm p-4 mb-4 border border-purple-100">
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-4 gap-3">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl shadow-lg shadow-indigo-200">
                                <FileText className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-gray-800">Chi Tiết Xuất Phụ Phẩm</h1>
                                <p className="text-xs text-gray-500 mt-0.5">Báo cáo chi tiết từng mặt hàng xuất kho</p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={fetchData} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium text-gray-600">
                                <RefreshCw className="w-4 h-4" /> <span className="hidden sm:inline">Làm mới</span>
                            </button>
                            <button onClick={handleExportExcel} className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors shadow-md shadow-green-200 flex items-center gap-2 text-sm font-medium">
                                <Download className="w-4 h-4" /> Xuất Excel
                            </button>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex items-center gap-4">
                            <div className="p-3 bg-blue-50 rounded-lg"><Package className="w-6 h-6 text-blue-600" /></div>
                            <div>
                                <p className="text-gray-500 text-xs font-medium uppercase">Tổng khối lượng</p>
                                <h3 className="text-2xl font-bold text-gray-800">{formatNumber(stats.totalVolume, 2)} <span className="text-sm text-gray-500">KG</span></h3>
                            </div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex items-center gap-4">
                            <div className="p-3 bg-green-50 rounded-lg"><TrendingUp className="w-6 h-6 text-green-600" /></div>
                            <div>
                                <p className="text-gray-500 text-xs font-medium uppercase">Tổng thành tiền</p>
                                <h3 className="text-2xl font-bold text-gray-800">{formatCurrency(stats.totalMoney)}</h3>
                            </div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex items-center gap-4">
                            <div className="p-3 bg-purple-50 rounded-lg"><FileText className="w-6 h-6 text-purple-600" /></div>
                            <div>
                                <p className="text-gray-500 text-xs font-medium uppercase">Số dòng chi tiết</p>
                                <h3 className="text-2xl font-bold text-gray-800">{stats.rowCount}</h3>
                            </div>
                        </div>
                    </div>

                    {/* Filters */}
                    {/* Filters */}
                    <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
                        <div className="relative w-full md:w-80">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Tìm số phiếu, khách hàng, tên hàng..."
                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>

                        <div className="w-full md:w-auto">
                            <div className="bg-white border border-gray-300 rounded-lg p-1.5 shadow-sm flex flex-col sm:flex-row gap-2 sm:gap-0 sm:items-center">
                                <div className="flex items-center gap-2 px-2">
                                    <Calendar className="w-4 h-4 text-gray-500 shrink-0" />
                                    <span className="text-xs text-gray-500 whitespace-nowrap">Từ</span>
                                    <input
                                        type="date"
                                        value={dateRange.startDate}
                                        onChange={e => setDateRange({ ...dateRange, startDate: e.target.value })}
                                        className="text-sm border-none focus:ring-0 p-0 text-gray-700 w-full sm:w-auto bg-transparent outline-none"
                                    />
                                </div>
                                <div className="hidden sm:block w-px h-4 bg-gray-300 mx-2"></div>
                                <div className="flex items-center gap-2 px-2 border-t border-gray-100 sm:border-0 pt-2 sm:pt-0">
                                    <span className="text-xs text-gray-500 whitespace-nowrap sm:hidden">Đến ngày</span>
                                    <span className="text-xs text-gray-500 whitespace-nowrap hidden sm:inline">Đến</span>
                                    <input
                                        type="date"
                                        value={dateRange.endDate}
                                        onChange={e => setDateRange({ ...dateRange, endDate: e.target.value })}
                                        className="text-sm border-none focus:ring-0 p-0 text-gray-700 w-full sm:w-auto bg-transparent outline-none"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Desktop Table */}
                    <div className="hidden md:block border border-gray-200 rounded-lg overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100 w-12">STT</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleRequestSort('SOPHIEU')}>Số phiếu <SortIcon column="SOPHIEU" /></th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleRequestSort('NGAY_XUAT')}>Ngày Xuất <SortIcon column="NGAY_XUAT" /></th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleRequestSort('NCC_KHACHHANG')}>Khách hàng <SortIcon column="NCC_KHACHHANG" /></th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleRequestSort('TEN_HANG')}>Tên hàng <SortIcon column="TEN_HANG" /></th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleRequestSort('KHOILUONG')}>Khối lượng <SortIcon column="KHOILUONG" /></th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleRequestSort('DONGIA')}>Đơn giá <SortIcon column="DONGIA" /></th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleRequestSort('THANHTIEN')}>Thành tiền <SortIcon column="THANHTIEN" /></th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {paginatedItems.length > 0 ? (
                                    paginatedItems.map((item, index) => (
                                        <tr key={index} className="hover:bg-indigo-50 transition-colors">
                                            <td className="px-4 py-2 text-center text-xs text-gray-500">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                            <td className="px-4 py-2 text-sm font-medium text-gray-800">{item.SOPHIEU}</td>
                                            <td className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">{formatDate(item.NGAY_XUAT)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-600">{item.NCC_KHACHHANG}</td>
                                            <td className="px-4 py-2 text-sm text-gray-600 font-medium">{item.TEN_HANG}</td>
                                            <td className="px-4 py-2 text-sm text-gray-800 text-right">{formatNumber(item.KHOILUONG, 2)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-600 text-right">{formatNumber(item.DONGIA)}</td>
                                            <td className="px-4 py-2 text-sm font-bold text-green-600 text-right">{formatCurrency(item.THANHTIEN)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="px-4 py-8 text-center text-gray-500 italic">Không có dữ liệu trong khoảng thời gian này</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-4">
                        {paginatedItems.length > 0 ? (
                            paginatedItems.map((item, index) => (
                                <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 active:scale-[0.98] transition-transform">
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="flex items-center gap-2">
                                            <div className="p-2 bg-indigo-50 rounded-lg">
                                                <span className="text-xs font-bold text-indigo-600">#{(currentPage - 1) * itemsPerPage + index + 1}</span>
                                            </div>
                                            <div>
                                                <span className="block text-sm font-bold text-gray-800">{item.SOPHIEU}</span>
                                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" /> {formatDate(item.NGAY_XUAT)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-3 space-y-1">
                                        <div>
                                            <p className="text-xs text-gray-500 uppercase">Khách hàng</p>
                                            <p className="text-sm font-semibold text-gray-800 line-clamp-1">{item.NCC_KHACHHANG}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 uppercase">Tên hàng</p>
                                            <p className="text-sm font-medium text-gray-700">{item.TEN_HANG}</p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100">
                                        <div>
                                            <p className="text-xs text-gray-500 mb-1">Khối lượng</p>
                                            <p className="text-sm font-bold text-blue-600">{formatNumber(item.KHOILUONG, 2)} KG</p>
                                            <p className="text-xs text-gray-400 mt-0.5">Giá: {formatNumber(item.DONGIA)}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs text-gray-500 mb-1">Thành tiền</p>
                                            <p className="text-sm font-bold text-green-600">{formatCurrency(item.THANHTIEN)}</p>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8 text-gray-500 bg-white rounded-xl border border-gray-200">Không có dữ liệu</div>
                        )}
                    </div>

                    {/* Pagination Controls */}
                    <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <span>Hiển thị</span>
                            <select
                                value={itemsPerPage}
                                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                                className="border border-gray-300 rounded px-2 py-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                            >
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span>dòng / trang</span>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="p-2 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <span className="text-sm text-gray-600">
                                Trang {currentPage} / {totalPages || 1}
                            </span>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className="p-2 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default XuatPhuPhamChiTiet;
